export const WATERMARK_CANVAS_SIZE = 800;
export const WATERMARK_CANVAS_3_4 = { width: 750, height: 1000 };

export function normalizeCanvasPortal(portalOrSize) {
    if (typeof portalOrSize === 'number') {
        return { width: portalOrSize, height: portalOrSize };
    }
    if (portalOrSize && typeof portalOrSize === 'object') {
        return {
            width: portalOrSize.width || WATERMARK_CANVAS_SIZE,
            height: portalOrSize.height || WATERMARK_CANVAS_SIZE,
        };
    }
    return { width: WATERMARK_CANVAS_SIZE, height: WATERMARK_CANVAS_SIZE };
}
const DEBUG_PREFIX = '[YKT水印]';

export function enableYktDebug() {
    try {
        window.localStorage.setItem('xxdebug', '1');
    } catch (e) {
        console.warn(DEBUG_PREFIX, '无法开启易可图 SDK 调试', e);
    }
}

export function logYktDebug(step, payload) {
    const entry = { step, time: new Date().toISOString(), ...payload };
    if (!window.__YKT_WATERMARK_DEBUG__) {
        window.__YKT_WATERMARK_DEBUG__ = [];
    }
    window.__YKT_WATERMARK_DEBUG__.push(entry);
    console.groupCollapsed(`${DEBUG_PREFIX} ${step}`);
    if (payload != null) {
        console.log(payload);
    }
    console.groupEnd();
}

/** 控制台打印易可图 serializeData / pageData */
export function logYktSerializeData(scene, serializeData, extra) {
    const childrenCount = serializeData?.serialization?.children?.length ?? 0;
    const label = `${DEBUG_PREFIX} ${scene} serializeData`;
    console.group(label);
    console.log('serializeData 对象:', serializeData);
    console.log('serialization.children 数量:', childrenCount);
    console.log('结构摘要:', summarizePeanutConfig(serializeData));
    if (extra != null) {
        console.log('附加信息:', extra);
    }
    try {
        console.log('serializeData JSON:', JSON.stringify(serializeData, null, 2));
    } catch (e) {
        console.warn('serializeData 无法 JSON 序列化', e);
    }
    console.groupEnd();
    if (String(scene).includes('save')) {
        window.__YKT_SAVE_PAGE_DATA__ = serializeData;
        window.__YKT_SAVE_PAYLOAD__ = extra?.fullSavePayload ?? null;
    } else if (String(scene).includes('getSerializeData')) {
        window.__YKT_SAVE_GET_SERIALIZE_DATA__ = serializeData;
    } else if (String(scene).includes('loadByConfig')) {
        window.__YKT_LOAD_BY_CONFIG_SERIALIZE_DATA__ = serializeData;
    }
}

function stripVolatileSerializeFields(data) {
    if (!data || typeof data !== 'object') return data;
    const cloned = JSON.parse(JSON.stringify(data));
    ['image', 'images', 'previewImage'].forEach((key) => {
        if (key in cloned) delete cloned[key];
    });
    return cloned;
}

/** 对比 save.pageData 与 getSerializeData 返回值 */
export function logYktSerializeDataCompare(pageData, serializeFromApi) {
    const label = `${DEBUG_PREFIX} save.pageData vs getSerializeData 对比`;
    const aChildren = pageData?.serialization?.children?.length ?? 0;
    const bChildren = serializeFromApi?.serialization?.children?.length ?? 0;
    const aKeys = pageData ? Object.keys(pageData).sort().join(',') : '';
    const bKeys = serializeFromApi ? Object.keys(serializeFromApi).sort().join(',') : '';
    let structureJsonEqual = false;
    try {
        structureJsonEqual = JSON.stringify(stripVolatileSerializeFields(pageData))
            === JSON.stringify(stripVolatileSerializeFields(serializeFromApi));
    } catch (e) {
        console.warn(label, '结构 JSON 对比失败', e);
    }
    console.group(label);
    console.log('save.pageData 摘要:', summarizePeanutConfig(pageData));
    console.log('getSerializeData 摘要:', summarizePeanutConfig(serializeFromApi));
    console.log('topKeys 相同:', aKeys === bKeys, { save: aKeys, getSerializeData: bKeys });
    console.log('serialization.children 数量:', aChildren, 'vs', bChildren, aChildren === bChildren ? '✓ 相同' : '✗ 不同');
    console.log('去除 image/images/previewImage 后 JSON 相同:', structureJsonEqual ? '✓ 相同' : '✗ 不同');
    if (!structureJsonEqual) {
        console.log('save.pageData (去预览字段):', stripVolatileSerializeFields(pageData));
        console.log('getSerializeData (去预览字段):', stripVolatileSerializeFields(serializeFromApi));
    }
    console.groupEnd();
    window.__YKT_SAVE_SERIALIZE_COMPARE__ = {
        topKeysEqual: aKeys === bKeys,
        childrenCountEqual: aChildren === bChildren,
        structureJsonEqual,
    };
}

function countSerializationChildren(config) {
    const roots = getSerializationRoots(config);
    return roots.reduce((sum, root) => sum + (root?.children?.length ?? 0), 0);
}

export function summarizePeanutConfig(config) {
    if (!config || typeof config !== 'object') {
        return { type: typeof config, value: config };
    }
    const materials = findMaterialsArray(config);
    const pictureLayers = [];
    walkObject(config, (node) => {
        if (!node || typeof node !== 'object') return;
        if (node.image && typeof node.image === 'object') {
            pictureLayers.push({
                type: node.type,
                left: node.left,
                top: node.top,
                width: node.width,
                height: node.height,
                imageUrl: node.image.url || node.image.src,
                imageWidth: node.image.width,
                imageHeight: node.image.height,
            });
        }
    });
    return {
        topKeys: Object.keys(config),
        portal: config.portal,
        materialsCount: materials?.length ?? 0,
        serializationChildrenCount: countSerializationChildren(config),
        topChildrenCount: Array.isArray(config.children) ? config.children.length : 0,
        topChildrenTypes: Array.isArray(config.children)
            ? config.children.map((c) => c?.type).filter(Boolean)
            : [],
        pageDataLayoutsCount: config?.pageData?.layouts?.length ?? 0,
        pictureLayers,
    };
}

export function withCacheBust(url) {
    if (!url) return url;
    return url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;
}

export function loadImageMeta(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            resolve({
                width: img.naturalWidth || img.width,
                height: img.naturalHeight || img.height,
            });
        };
        img.onerror = () => reject(new Error('图片加载失败'));
        img.src = withCacheBust(url);
    });
}

export function calcCenteredRect(imgW, imgH, canvasSize = WATERMARK_CANVAS_SIZE) {
    const scale = Math.min(canvasSize / imgW, canvasSize / imgH, 1);
    const width = Math.round(imgW * scale);
    const height = Math.round(imgH * scale);
    return {
        left: Math.round((canvasSize - width) / 2),
        top: Math.round((canvasSize - height) / 2),
        width,
        height,
    };
}

const MATERIAL_ARRAY_KEYS = ['materials', 'extraMaterials'];

function walkObject(node, visitor) {
    if (!node || typeof node !== 'object') return;
    visitor(node);
    if (Array.isArray(node)) {
        node.forEach((item) => walkObject(item, visitor));
        return;
    }
    Object.values(node).forEach((value) => walkObject(value, visitor));
}

function genLayerId() {
    return `wm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function genUuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** 易可图海报 serializeData 的图层树在 serialization.children */
function getSerializationRoots(config) {
    const roots = [];
    if (config?.serialization && typeof config.serialization === 'object') {
        roots.push(config.serialization);
    }
    const layouts = config?.pageData?.layouts;
    if (Array.isArray(layouts)) {
        layouts.forEach((layout) => {
            if (layout?.serialization && typeof layout.serialization === 'object') {
                roots.push(layout.serialization);
            } else if (layout && typeof layout === 'object' && Array.isArray(layout.children)) {
                roots.push(layout);
            }
        });
    }
    return roots;
}

/** 判断是否为 BitImage 顶层 children 结构（易可图 loadByConfig 可用） */
export function isBitImageSerializeData(data) {
    if (!data || typeof data !== 'object') return false;
    if (!Array.isArray(data.children) || !data.children.length) return false;
    return data.children.some(
        (child) => child?.type === 'BitImage'
            && child?.attrs
            && (typeof child.attrs.url === 'string' || child.attrs.width != null)
    );
}

/** 刷新 BitImage serializeData：仅替换 attrs.url，其余 attrs 字段原样保留 */
export function refreshBitImageSerializeData(data, sourceImageUrl) {
    const cloned = JSON.parse(JSON.stringify(data));
    if (!Array.isArray(cloned.children)) return cloned;
    const url = sourceImageUrl
        ? withCacheBust(stripUrlQuery(sourceImageUrl))
        : null;
    cloned.children.forEach((child) => {
        if (!child?.attrs) return;
        if (url) {
            child.attrs.url = url;
        } else if (typeof child.attrs.url === 'string' && /^https?:/i.test(child.attrs.url)) {
            child.attrs.url = withCacheBust(stripUrlQuery(child.attrs.url));
        }
    });
    return cloned;
}

/** 深拷贝 attrs，保留 crop / flipX / flipY 等全部字段 */
function cloneBitImageAttrs(attrs) {
    if (!attrs || typeof attrs !== 'object') return {};
    return JSON.parse(JSON.stringify(attrs));
}

/**
 * 从易可图 save 的 pageData.serialization 提取完整 attrs，构建 loadByConfig 用的 BitImage 结构
 */
export function buildBitImageSerializeDataFromSavePageData(savePageData, sourceImageUrl, fallbackPortal) {
    const serialization = savePageData?.serialization;
    if (!serialization?.children?.length) {
        return null;
    }
    const portal = normalizeCanvasPortal(serialization.portal || fallbackPortal);
    const sourceUrl = sourceImageUrl
        ? withCacheBust(stripUrlQuery(sourceImageUrl))
        : null;
    const children = serialization.children.map((child) => {
        const attrs = cloneBitImageAttrs(child.attrs);
        
        if (sourceUrl) {
            attrs.url = sourceUrl;
        } else if (typeof attrs.url === 'string' && /^https?:/i.test(attrs.url)) {
            attrs.url = withCacheBust(stripUrlQuery(attrs.url));
        }
        return {
            type: child.type || 'BitImage',
            id: child.id || genUuid(),
            attrs,
        };
    });
    return {
        uuid: savePageData.uuid || genUuid(),
        portal: { width: portal.width, height: portal.height },
        children,
    };
}

/** 从 submit 结果或已存 pageData 中取出 BitImage 结构 */
export function pickBitImagePageData(raw, sourceImageUrl, fallbackPortal) {
    console.log('@@@@@@@@@@@@@@@@@@!!!!!!raw', raw);
    if (isBitImageSerializeData(raw)) {
        return JSON.parse(JSON.stringify(raw));
    }
    if (raw?.serialization?.children?.length) {
        return buildBitImageSerializeDataFromSavePageData(raw, sourceImageUrl, fallbackPortal);
    }
    return null;
}

/** 准备 loadByConfig 参数：完整 attrs + 原始素材 url */
export function prepareBitImageSerializeDataForLoad(pageData, sourceImageUrl) {
    const picked = pickBitImagePageData(pageData, sourceImageUrl, null);
    if (!picked) return null;
    return refreshBitImageSerializeData(picked, sourceImageUrl);
}

/** 判断是否为可 loadByConfig 恢复的 serializeData */
export function isValidWatermarkSerializeData(data) {
    return isBitImageSerializeData(data);
}

function ensureSerializationChildren(serialization) {
    if (!serialization.children || !Array.isArray(serialization.children)) {
        serialization.children = [];
    }
    return serialization.children;
}

/** 从已保存的 pageData 中找图片图层节点模板 */
function findPictureChildTemplate(config) {
    let template = null;
    walkObject(config, (node) => {
        if (template) return;
        if (!node || typeof node !== 'object') return;
        if (node.isBackground || node.isPortalBackground) return;
        const type = String(node.type || node.materialType || node.category || '').toLowerCase();
        if (type.includes('background')) return;
        const isPicture = type.includes('picture') || type.includes('image') || type.includes('img');
        if (!isPicture) return;
        const img = node.image || node.resource || node.src;
        if (!img) return;
        if (node.left == null && node.top == null && !node.transform) return;
        template = node;
    });
    return template;
}

function findMaterialsArray(config) {
    if (!config || typeof config !== 'object') return null;
    for (const key of MATERIAL_ARRAY_KEYS) {
        if (Array.isArray(config[key])) {
            return config[key];
        }
    }
    let found = null;
    walkObject(config, (node) => {
        if (found || !node || typeof node !== 'object') return;
        for (const key of MATERIAL_ARRAY_KEYS) {
            if (Array.isArray(node[key])) {
                found = node[key];
                return;
            }
        }
    });
    return found;
}

function ensurePortal(config, canvasSize) {
    if (!config.portal || typeof config.portal !== 'object') {
        config.portal = { width: canvasSize, height: canvasSize };
    }
    config.portal.width = canvasSize;
    config.portal.height = canvasSize;
    getSerializationRoots(config).forEach((root) => {
        if (!root.portal || typeof root.portal !== 'object') {
            root.portal = { width: canvasSize, height: canvasSize };
        }
        root.portal.width = canvasSize;
        root.portal.height = canvasSize;
    });
}

function applyImageObject(imageObj, url, width, height, naturalWidth, naturalHeight) {
    imageObj.url = url;
    imageObj.src = url;
    imageObj.width = width;
    imageObj.height = height;
    if (imageObj.naturalWidth != null || naturalWidth != null) {
        imageObj.naturalWidth = naturalWidth ?? width;
    }
    if (imageObj.naturalHeight != null || naturalHeight != null) {
        imageObj.naturalHeight = naturalHeight ?? height;
    }
}

function applyRectToNode(node, rect) {
    const { left, top, width, height } = rect;
    node.left = left;
    node.top = top;
    node.width = width;
    node.height = height;
    if (node.transform && typeof node.transform === 'object') {
        node.transform.left = left;
        node.transform.top = top;
        node.transform.width = width;
        node.transform.height = height;
    }
}

function createPictureChild(template, url, rect, naturalSize) {
    const { width, height } = rect;
    if (template) {
        const child = JSON.parse(JSON.stringify(template));
        ['uuid', 'id', '_id', 'layerId', 'key'].forEach((k) => delete child[k]);
        child.uuid = genLayerId();
        if (child.image && typeof child.image === 'object') {
            applyImageObject(child.image, url, width, height, naturalSize.width, naturalSize.height);
        } else if (child.resource && typeof child.resource === 'object') {
            applyImageObject(child.resource, url, width, height, naturalSize.width, naturalSize.height);
        } else {
            child.image = {
                url,
                src: url,
                width,
                height,
                naturalWidth: naturalSize.width,
                naturalHeight: naturalSize.height,
            };
        }
        applyRectToNode(child, rect);
        return child;
    }
    return {
        type: 'picture',
        uuid: genLayerId(),
        left: rect.left,
        top: rect.top,
        width,
        height,
        rotate: 0,
        opacity: 1,
        visible: true,
        lock: false,
        transform: {
            left: rect.left,
            top: rect.top,
            width,
            height,
            rotate: 0,
            scaleX: 1,
            scaleY: 1,
        },
        image: {
            url,
            src: url,
            width,
            height,
            naturalWidth: naturalSize.width,
            naturalHeight: naturalSize.height,
        },
    };
}

function insertPictureIntoSerialization(config, url, rect, naturalSize, template) {
    const child = createPictureChild(template, url, rect, naturalSize);
    const roots = getSerializationRoots(config);
    if (!roots.length) {
        config.serialization = { children: [child], portal: { ...config.portal } };
        roots.push(config.serialization);
    }
    roots.forEach((root) => {
        ensureSerializationChildren(root).push(JSON.parse(JSON.stringify(child)));
    });
    return child;
}

/**
 * 基于易可图 getSerializeData / save.pageData，插入用户水印图片图层
 * 图层写入 serialization.children（易可图实际渲染树）
 */
export async function buildPeanutConfigWithImage(imageUrl, baseConfig, canvasSize = WATERMARK_CANVAS_SIZE) {
    const config = JSON.parse(JSON.stringify(baseConfig || {}));
    const url = withCacheBust(imageUrl);
    let rect;
    let naturalSize = { width: canvasSize, height: canvasSize };
    let imageMetaError = null;
    try {
        const meta = await loadImageMeta(url);
        naturalSize = meta;
        rect = calcCenteredRect(meta.width, meta.height, canvasSize);
        logYktDebug('图片尺寸', { url, meta, rect });
    } catch (err) {
        imageMetaError = err?.message || String(err);
        rect = calcCenteredRect(canvasSize * 0.6, canvasSize * 0.6, canvasSize);
        logYktDebug('图片尺寸读取失败，使用默认区域', { url, imageMetaError, rect });
    }
    ensurePortal(config, canvasSize);

    const template = findPictureChildTemplate(baseConfig);
    const child = insertPictureIntoSerialization(config, url, rect, naturalSize, template);

    const summary = {
        strategy: 'push-serialization-children',
        imageUrl: url,
        imageMetaError,
        clonedFromTemplate: !!template,
        childType: child.type,
        baseSummary: summarizePeanutConfig(baseConfig),
        resultSummary: summarizePeanutConfig(config),
    };
    logYktDebug('buildPeanutConfigWithImage', summary);
    return { config, debug: summary };
}

export function getPosterLoadOptions(canvasSize = WATERMARK_CANVAS_SIZE) {
    return {
        extendSize: [{ width: canvasSize, height: canvasSize }],
    };
}

export function getPosterLoadOptionsForPortal(portal) {
    const width = portal?.width || WATERMARK_CANVAS_SIZE;
    const height = portal?.height || WATERMARK_CANVAS_SIZE;
    return {
        extendSize: [{ width, height }],
    };
}

function stripUrlQuery(url) {
    if (typeof url !== 'string') return url;
    return url.replace(/[?#].*$/, '');
}

/**
 * 同步构建 BitImage serializeData（保存后写入模板用）
 * fillPortal=true：图片铺满画布（x/y=0，与易可图 save 结构一致）
 */
export function buildBitImageSerializeDataSync(imageUrl, portalOrSize = WATERMARK_CANVAS_SIZE, options = {}) {
    const { fillPortal = false } = options;
    const portal = normalizeCanvasPortal(portalOrSize);
    const url = withCacheBust(stripUrlQuery(imageUrl));
    let imgWidth = portal.width;
    let imgHeight = portal.height;
    let x = 0;
    let y = 0;
    if (!fillPortal) {
        x = Math.round((portal.width - imgWidth) / 2);
        y = Math.round((portal.height - imgHeight) / 2);
    }
    return {
        uuid: genUuid(),
        portal: { width: portal.width, height: portal.height },
        children: [
            {
                type: 'BitImage',
                id: genUuid(),
                attrs: {
                    width: imgWidth,
                    height: imgHeight,
                    x,
                    y,
                    rotate: 0,
                    url,
                },
            },
        ],
    };
}

/** 为 serializeData 内远程图片 URL 加 cache bust */
export function refreshSerializeDataUrls(data) {
    if (isBitImageSerializeData(data)) {
        return refreshBitImageSerializeData(data);
    }
    if (!data || typeof data !== 'object') return data;
    const cloned = JSON.parse(JSON.stringify(data));
    walkObject(cloned, (node) => {
        if (!node || typeof node !== 'object') return;
        ['url', 'src'].forEach((key) => {
            if (typeof node[key] === 'string' && /^https?:/i.test(node[key])) {
                node[key] = withCacheBust(stripUrlQuery(node[key]));
            }
        });
        if (node.attrs?.url && /^https?:/i.test(node.attrs.url)) {
            node.attrs.url = withCacheBust(stripUrlQuery(node.attrs.url));
        }
    });
    return cloned;
}

/**
 * 按易可图 save 返回的 BitImage 顶层 children 结构构建 serializeData
 * 画布默认 800×800，3:4 水印传 { width: 750, height: 1000 }
 */
export async function buildBitImageSerializeData(imageUrl, portalOrSize = WATERMARK_CANVAS_SIZE, options = {}) {
    const { fillPortal = false } = options;
    const portal = normalizeCanvasPortal(portalOrSize);
    const url = withCacheBust(stripUrlQuery(imageUrl));
    let imgWidth = portal.width;
    let imgHeight = portal.height;
    let x = 0;
    let y = 0;
    let imageMetaError = null;
    if (!fillPortal) {
        try {
            const meta = await loadImageMeta(url);
            imgWidth = meta.width;
            imgHeight = meta.height;
            x = Math.round((portal.width - imgWidth) / 2);
            y = Math.round((portal.height - imgHeight) / 2);
            logYktDebug('BitImage 图片尺寸', { url, meta, portal });
        } catch (err) {
            imageMetaError = err?.message || String(err);
            imgWidth = Math.round(portal.width * 0.6);
            imgHeight = Math.round(portal.height * 0.6);
            x = Math.round((portal.width - imgWidth) / 2);
            y = Math.round((portal.height - imgHeight) / 2);
            logYktDebug('BitImage 图片尺寸读取失败，使用默认尺寸', { url, imageMetaError, portal });
        }
    } else {
        logYktDebug('BitImage fillPortal', { url, portal });
    }
    const config = {
        uuid: genUuid(),
        portal: { width: portal.width, height: portal.height },
        children: [
            {
                type: 'BitImage',
                id: genUuid(),
                attrs: {
                    width: imgWidth,
                    height: imgHeight,
                    x,
                    y,
                    rotate: 0,
                    url,
                },
            },
        ],
    };
    const summary = {
        strategy: 'bit-image-top-children',
        imageUrl: url,
        portal,
        imageSize: { width: imgWidth, height: imgHeight },
        position: { x, y },
        imageMetaError,
        resultSummary: summarizePeanutConfig(config),
    };
    logYktDebug('buildBitImageSerializeData', summary);
    return { config, debug: summary };
}
